"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

interface SiteNavbarSession {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
}

interface SiteNavbarProps {
  session?: SiteNavbarSession | null;
  rightSlot?: React.ReactNode;
}

// Top coins for the live ticker strip
const TICKER_COINS = [
  "BTCUSDT", "ETHUSDT", "SOLUSDT", "BNBUSDT", "XRPUSDT",
  "ADAUSDT", "DOGEUSDT", "DOTUSDT", "AVAXUSDT", "LINKUSDT",
];

const SYMBOL_LABELS: Record<string, string> = {
  BTCUSDT: "BTC", ETHUSDT: "ETH", SOLUSDT: "SOL", BNBUSDT: "BNB",
  XRPUSDT: "XRP", ADAUSDT: "ADA", DOGEUSDT: "DOGE", DOTUSDT: "DOT",
  AVAXUSDT: "AVAX", LINKUSDT: "LINK",
};

interface TickerPrice {
  symbol: string;
  price: number;
  changePercent: number;
}

const navLinks = [
  { href: "/token", label: "Tokens", icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
  { href: "/chain", label: "Chains", icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" },
  { href: "/developer-activity", label: "Dev Activity", icon: "M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" },
  { href: "/fear-greed", label: "Fear & Greed", icon: "M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" },
];

function useLiveTicker() {
  const [prices, setPrices] = useState<Map<string, TickerPrice>>(new Map());
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const streams = TICKER_COINS.map((s) => `${s.toLowerCase()}@ticker`).join("/");
    const ws = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data as string) as {
        data: { s: string; c: string; P: string };
      };
      const d = msg.data;
      const price = parseFloat(d.c);
      const changePercent = parseFloat(d.P);
      if (isNaN(price)) return;
      setPrices((prev) => {
        const next = new Map(prev);
        next.set(d.s, {
          symbol: d.s,
          price,
          changePercent: isNaN(changePercent) ? 0 : changePercent,
        });
        return next;
      });
    };

    return () => {
      ws.close();
    };
  }, []);

  return prices;
}

function formatTickerPrice(price: number): string {
  if (price < 1) return `$${price.toFixed(4)}`;
  if (price < 100) return `$${price.toFixed(2)}`;
  return `$${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function SiteNavbar({ session = null, rightSlot }: SiteNavbarProps) {
  const [mounted, setMounted] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const prices = useLiveTicker();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <header className="sticky top-0 z-50">
      {/* Live Price Ticker Strip */}
      <div className="border-b border-[hsl(var(--border))]/50 bg-[hsl(230,15%,4%)]/95 backdrop-blur-md overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="flex items-center gap-6 py-1.5 overflow-x-auto scrollbar-hide text-xs">
            {TICKER_COINS.map((sym) => {
              const data = mounted ? prices.get(sym) : undefined;
              const label = SYMBOL_LABELS[sym] ?? sym;
              return (
                <div key={sym} className="flex items-center gap-1.5 shrink-0">
                  <span className="text-gray-500 font-medium">{label}</span>
                  <span className="text-white font-mono">
                    {data ? formatTickerPrice(data.price) : "--"}
                  </span>
                  {data && !isNaN(data.changePercent) && data.changePercent !== 0 && (
                    <span
                      className={`font-mono ${
                        data.changePercent >= 0
                          ? "text-emerald-400"
                          : "text-red-400"
                      }`}
                    >
                      {data.changePercent >= 0 ? "+" : ""}
                      {data.changePercent.toFixed(2)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className={`border-b transition-all duration-200 ${
          scrolled
            ? "border-[hsl(var(--border))] bg-[hsl(230,15%,5%)]/95 backdrop-blur-xl shadow-lg shadow-black/20"
            : "border-transparent bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Brand */}
            <Link href="/" className="flex items-center gap-2.5 shrink-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <svg
                  className="w-4.5 h-4.5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"
                  />
                </svg>
              </div>
              <span className="text-lg font-bold tracking-tight hidden sm:inline">
                <span className="text-white">Developer</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Activity</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = pathname === link.href || pathname?.startsWith(link.href + "/");
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? "bg-blue-500/15 text-blue-400"
                        : "text-gray-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d={link.icon} />
                    </svg>
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2">
              {/* Auth / Dashboard */}
              {rightSlot ? (
                rightSlot
              ) : session ? (
                <Link
                  href="/dashboard"
                  className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-lg text-sm font-medium transition-all shadow-lg shadow-blue-600/20"
                >
                  {session.image ? (
                    <Image
                      src={session.image}
                      alt={session.name || "User"}
                      width={20}
                      height={20}
                      className="rounded-full ring-2 ring-white/20"
                    />
                  ) : (
                    <div className="w-5 h-5 bg-blue-400/30 rounded-full flex items-center justify-center text-xs font-bold">
                      {session.name?.charAt(0) || session.email?.charAt(0) || "U"}
                    </div>
                  )}
                  <span className="hidden sm:inline">Dashboard</span>
                </Link>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    href="/auth/signin"
                    className="px-3.5 py-2 text-gray-400 hover:text-white text-sm font-medium transition-colors hidden sm:block"
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/auth/signup"
                    className="px-3.5 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-lg text-sm font-medium transition-all shadow-lg shadow-blue-600/20"
                  >
                    Get Started
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  {mobileOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-[hsl(var(--border))] bg-[hsl(230,15%,5%)]/98 backdrop-blur-xl">
            <div className="max-w-[1400px] mx-auto px-4 py-4 space-y-1">
              {navLinks.map((link) => {
                const isActive = pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? "bg-blue-500/15 text-blue-400"
                        : "text-gray-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d={link.icon} />
                    </svg>
                    {link.label}
                  </Link>
                );
              })}
              <div className="pt-3 border-t border-[hsl(var(--border))]">
                {session ? (
                  <Link
                    href="/dashboard"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl text-sm font-medium"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <div className="space-y-2">
                    <Link
                      href="/auth/signin"
                      className="block text-center px-4 py-3 text-gray-400 hover:text-white rounded-xl text-sm font-medium transition-colors"
                    >
                      Sign In
                    </Link>
                    <Link
                      href="/auth/signup"
                      className="block text-center px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl text-sm font-medium"
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
